import { useEffect, useState } from 'react';
import { RefreshCw, Activity, Plus, Pencil, Trash2, FileText, Receipt } from 'lucide-react';
import { projectoActivityService } from '../../services/projectoService';
import type { ProjectoActivityLog } from '../../types/projecto';

interface Props {
  projectoId: string;
}

const ACTION_CONFIG: Record<string, { label: string; color: string; Icon: React.ElementType }> = {
  created:         { label: 'Projeto criado',        color: 'text-emerald-700 bg-emerald-50',  Icon: Plus },
  updated:         { label: 'Projeto atualizado',    color: 'text-blue-700 bg-blue-50',        Icon: Pencil },
  deleted:         { label: 'Projeto eliminado',     color: 'text-red-700 bg-red-50',          Icon: Trash2 },
  field_changed:   { label: 'Campo alterado',        color: 'text-gray-700 bg-gray-50',        Icon: Pencil },
  item_added:      { label: 'Linha adicionada',      color: 'text-sky-700 bg-sky-50',          Icon: Plus },
  item_removed:    { label: 'Linha removida',        color: 'text-red-700 bg-red-50',          Icon: Trash2 },
  invoice_added:   { label: 'Fatura adicionada',     color: 'text-teal-700 bg-teal-50',        Icon: Receipt },
  invoice_updated: { label: 'Fatura atualizada',     color: 'text-teal-700 bg-teal-50',        Icon: FileText },
};

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'agora mesmo';
  if (mins < 60) return `há ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `há ${hrs} h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return days === 1 ? 'há 1 dia' : `há ${days} dias`;
  return new Date(dateStr).toLocaleDateString('pt-PT');
}

export function ProjectoActivityTab({ projectoId }: Props) {
  const [logs, setLogs] = useState<ProjectoActivityLog[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await projectoActivityService.getByProjectoId(projectoId);
      setLogs(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [projectoId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <Activity className="w-10 h-10 mb-3 opacity-30" />
        <p className="text-sm font-medium">Ainda sem atividade</p>
        <p className="text-xs mt-1">As alterações a este projeto aparecem aqui.</p>
      </div>
    );
  }

  return (
    <div className="space-y-1 pb-24">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700">Histórico</h3>
        <button
          onClick={load}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          title="Atualizar"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="relative">
        <div className="absolute left-5 top-0 bottom-0 w-px bg-gray-100" />

        <div className="space-y-3">
          {logs.map((log) => {
            const cfg = ACTION_CONFIG[log.action] ?? {
              label: log.action,
              color: 'text-gray-600 bg-gray-50',
              Icon: Activity,
            };
            const { Icon } = cfg;

            return (
              <div key={log.id} className="relative flex items-start gap-4 pl-10">
                <div className={`absolute left-3 top-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.color}`}>
                  <Icon className="w-2.5 h-2.5" />
                </div>

                <div className="flex-1 min-w-0 bg-white rounded-lg border border-gray-100 px-4 py-3 shadow-sm">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-900">{cfg.label}</span>
                      {log.user_email && (
                        <span className="text-xs text-gray-500 ml-2">por {log.user_email}</span>
                      )}

                      {/* Only what reads as a sentence, not the raw field lists */}
                      {Boolean(log.details?.item_name || log.details?.number) && (
                        <p className="mt-1 text-xs text-gray-500">{String(log.details.item_name || log.details.number)}</p>
                      )}

                      {log.field_name && (
                        <div className="mt-1 text-xs text-gray-500">
                          <span className="font-medium">{log.field_name}</span>
                          {log.old_value && <> <span className="line-through text-red-400">{log.old_value}</span> → </>}
                          {log.new_value && <span className="text-emerald-600">{log.new_value}</span>}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">
                      {formatRelative(log.created_at)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
